import React, { useEffect, useState, useMemo, useContext } from "react";
import { usePersistentState } from "../../Common/usePersistentFilters";
import { Card, Row, Col, Button, Table, Badge, Form, Nav, Spinner, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../../api";
import PageTitle from "../../../layouts/PageTitle";
import usePermission from "../../../../hooks/usePermission";
import { useSelector } from "react-redux";
import { ThemeContext } from "../../../../context/ThemeContext";
import PortalMenu from "../../Common/PortalMenu";
import ReimbursementFormModal from "./ReimbursementFormModal";
import ReimbursementDetailModal from "./ReimbursementDetailModal";

const svgMenu = (
  <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <circle fill="#000000" cx="5" cy="12" r="2"></circle>
      <circle fill="#000000" cx="12" cy="12" r="2"></circle>
      <circle fill="#000000" cx="19" cy="12" r="2"></circle>
    </g>
  </svg>
);

const formatMoney = (val) => {
  const num = Number(val || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

const statusVariant = {
  PENDING: "warning",
  APPROVED: "info",
  SETTLED: "success",
  REJECTED: "danger",
  DRAFT: "secondary",
};

const CATEGORIES = [
  "ALL",
  "Travel & Transport",
  "Fuel & Mileage",
  "Meals & Food",
  "Hotel & Lodging",
  "Site Purchases & Tools",
  "Mobile & Internet",
  "Client Entertainment",
  "Medical / Emergency",
  "Other",
];

const ReimbursementPage = () => {
  const navigate = useNavigate();
  const { background } = useContext(ThemeContext);
  const isDark = background?.value === "dark";
  const { hasPermission, isAdmin } = usePermission();
  const userDetails = useSelector((state) => state.auth?.userDetails);
  const currentUser = userDetails?.user || userDetails || null;

  const canApprove = Boolean(isAdmin || hasPermission("approve:reimbursements"));
  const canReject = Boolean(isAdmin || hasPermission("reject:reimbursements"));
  const canReview = canApprove || canReject;
  const canSettle = Boolean(isAdmin || hasPermission("settle:reimbursements"));
  const canCreate = Boolean(isAdmin || hasPermission("create:reimbursements"));
  const canEdit = Boolean(isAdmin || hasPermission("edit:reimbursements"));
  const canDelete = Boolean(isAdmin || hasPermission("delete:reimbursements"));
  const canReadAll = Boolean(isAdmin || canReview || canSettle);

  // Active Tab: "my" (My Claims), "all" (All Claims), "settle" (Accounts Queue)
  const [activeTab, setActiveTab] = useState("my");

  // Claims Data & Stats
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState({
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    settled: { count: 0, amount: 0 },
    total: { count: 0, amount: 0 },
  });
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = usePersistentState("reimbursement_search", "");
  const [statusFilter, setStatusFilter] = usePersistentState("reimbursement_status", "ALL");
  const [categoryFilter, setCategoryFilter] = usePersistentState("reimbursement_category", "ALL");
  const [dateFrom, setDateFrom] = usePersistentState("reimbursement_date_from", "");
  const [dateTo, setDateTo] = usePersistentState("reimbursement_date_to", "");

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEditClaim, setSelectedEditClaim] = useState(null);
  const [selectedDetailClaim, setSelectedDetailClaim] = useState(null);

  // 1. Initial tab setup based on user role/permissions
  useEffect(() => {
    if (canReadAll && !canCreate && !isAdmin) {
      setActiveTab(canSettle && !canReview ? "settle" : "all");
    }
  }, [canReadAll, canSettle, canReview, canCreate, isAdmin]);

  // 2. Fetch Claims & Stats whenever tab or filters change
  useEffect(() => {
    loadClaims();
    loadStats();
  }, [activeTab, statusFilter, categoryFilter, dateFrom, dateTo]);

  const loadStats = async () => {
    try {
      const res = await api.get("/employee-reimbursements/summary/stats");
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.warn("Failed to load stats:", err);
    }
  };

  const loadClaims = async () => {
    try {
      setLoading(true);
      const params = {
        scope: activeTab === "my" ? "my" : "all",
      };

      if (activeTab === "settle") {
        params.status = "APPROVED";
      } else if (statusFilter !== "ALL") {
        params.status = statusFilter;
      }

      if (categoryFilter !== "ALL") params.category = categoryFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (search) params.search = search;

      const res = await api.get("/employee-reimbursements", { params });
      if (res.data.success) {
        setClaims(res.data.data || []);
      }
    } catch (err) {
      swal("Error", "Failed to load reimbursement claims", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClaim = (claim) => {
    swal({
      title: "Delete Claim?",
      text: `Are you sure you want to delete claim #${claim.claimId || claim._id}?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const res = await api.delete(`/employee-reimbursements/${claim._id}`);
          if (res.data.success) {
            swal("Deleted", "Claim deleted successfully", "success");
            loadClaims();
            loadStats();
          }
        } catch (err) {
          swal("Error", err.response?.data?.message || "Failed to delete claim", "error");
        }
      }
    });
  };

  const tabStyle = (tabKey) => {
    const isActive = activeTab === tabKey;
    return {
      fontSize: "14px",
      fontWeight: isActive ? "700" : "500",
      color: isActive ? (isDark ? "#38bdf8" : "#0f766e") : (isDark ? "#94a3b8" : "#64748b"),
      borderBottom: isActive ? (isDark ? "3px solid #38bdf8" : "3px solid #0f766e") : "3px solid transparent",
      backgroundColor: isActive ? (isDark ? "transparent" : "#ffffff") : "transparent",
      borderRadius: "6px 6px 0 0",
      transition: "all 0.15s ease",
      boxShadow: isActive && !isDark ? "0 -2px 6px rgba(0,0,0,0.04)" : "none"
    };
  };

  return (
    <div className="reimbursement-module">
      <PageTitle
        activeMenu="Reimbursements"
        motherMenu="HR & Payroll"
        pageContent="Employee Reimbursements & Expense Claims"
      />

      {/* Top Action Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-0 text-dark fw-bold">Employee Reimbursements</h4>
          <span className="text-muted small">
            Track, approve, and settle business expense claims with attached receipts
          </span>
        </div>
        <div className="d-flex gap-2">
          {canCreate && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setSelectedEditClaim(null);
                setShowCreateModal(true);
              }}
            >
              Raise Expense Claim
            </Button>
          )}
        </div>
      </div>

      {/* Summary KPI Cards */}
      <Row className="g-3 mb-3">
        <Col sm={6} lg={3}>
          <Card
            className="border-0 shadow-sm h-100"
            style={{
              borderLeft: "4px solid #f59e0b",
              borderRadius: "10px",
              minHeight: "125px",
            }}
          >
            <Card.Body className="p-3 d-flex flex-column justify-content-between">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                  Pending Review
                </div>
                <Badge bg="warning" text="dark" style={{ fontSize: "11px" }}>
                  Pending
                </Badge>
              </div>
              <div className="my-1">
                <h4 className="mb-0 fw-bold text-warning" style={{ fontSize: "1.45rem" }}>
                  {formatMoney(stats.pending.amount)}
                </h4>
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {stats.pending.count} {stats.pending.count === 1 ? "claim" : "claims"} pending review
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col sm={6} lg={3}>
          <Card
            className="border-0 shadow-sm h-100"
            style={{
              borderLeft: "4px solid #0ea5e9",
              borderRadius: "10px",
              minHeight: "125px",
            }}
          >
            <Card.Body className="p-3 d-flex flex-column justify-content-between">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                  Approved (Unpaid)
                </div>
                <Badge bg="info" style={{ fontSize: "11px" }}>
                  Approved
                </Badge>
              </div>
              <div className="my-1">
                <h4 className="mb-0 fw-bold text-info" style={{ fontSize: "1.45rem" }}>
                  {formatMoney(stats.approved.amount)}
                </h4>
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {stats.approved.count} {stats.approved.count === 1 ? "claim" : "claims"} ready for payout
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col sm={6} lg={3}>
          <Card
            className="border-0 shadow-sm h-100"
            style={{
              borderLeft: "4px solid #10b981",
              borderRadius: "10px",
              minHeight: "125px",
            }}
          >
            <Card.Body className="p-3 d-flex flex-column justify-content-between">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                  Settled & Paid
                </div>
                <Badge bg="success" style={{ fontSize: "11px" }}>
                  Settled
                </Badge>
              </div>
              <div className="my-1">
                <h4 className="mb-0 fw-bold text-success" style={{ fontSize: "1.45rem" }}>
                  {formatMoney(stats.settled.amount)}
                </h4>
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {stats.settled.count} {stats.settled.count === 1 ? "claim" : "claims"} disbursed
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col sm={6} lg={3}>
          <Card
            className="border-0 shadow-sm h-100"
            style={{
              borderLeft: "4px solid #64748b",
              borderRadius: "10px",
              minHeight: "125px",
            }}
          >
            <Card.Body className="p-3 d-flex flex-column justify-content-between">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                  Total Claims
                </div>
                <Badge bg="secondary" style={{ fontSize: "11px" }}>
                  Total
                </Badge>
              </div>
              <div className="my-1">
                <h4 className="mb-0 fw-bold" style={{ fontSize: "1.45rem" }}>
                  {formatMoney(stats.total.amount)}
                </h4>
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {stats.total.count} {stats.total.count === 1 ? "claim" : "claims"} raised
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Navigation Tabs (My Claims / All Staff Claims / Accounts Settlement Queue) */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="border-bottom p-2" style={{ backgroundColor: isDark ? "transparent" : "#f8fafc" }}>
          <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="border-0">
            <Nav.Item>
              <Nav.Link eventKey="my" className="px-4 py-2" style={tabStyle("my")}>
                My Expense Claims
              </Nav.Link>
            </Nav.Item>

            {canReadAll && (
              <Nav.Item>
                <Nav.Link eventKey="all" className="px-4 py-2" style={tabStyle("all")}>
                  All Staff Claims / Review
                  {stats.pending.count > 0 && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      {stats.pending.count}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            )}

            {(canSettle || canReadAll) && (
              <Nav.Item>
                <Nav.Link eventKey="settle" className="px-4 py-2" style={tabStyle("settle")}>
                  Accounts Settlement Queue
                  {stats.approved.count > 0 && (
                    <Badge bg="info" className="ms-2">
                      {stats.approved.count}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </Card.Header>

        <Card.Body>
          {/* Filter Row */}
          <Row className="g-2 mb-3 align-items-end">
            <Col lg={3} md={6}>
              <Form.Label className="small text-muted mb-1">Search</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Search Claim ID, Merchant..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadClaims()}
              />
            </Col>

            {activeTab !== "settle" && (
              <Col lg={2} md={3} xs={6}>
                <Form.Label className="small text-muted mb-1">Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="SETTLED">Settled / Paid</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="DRAFT">Draft</option>
                </Form.Select>
              </Col>
            )}

            <Col lg={2} md={3} xs={6}>
              <Form.Label className="small text-muted mb-1">Category</Form.Label>
              <Form.Select
                size="sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="ALL">All Categories</option>
                {Array.from(new Set(claims.map((c) => c.category).filter(Boolean)))
                  .sort()
                  .map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
              </Form.Select>
            </Col>

            <Col lg={2} md={3} xs={6}>
              <Form.Label className="small text-muted mb-1">From Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Col>

            <Col lg={2} md={3} xs={6}>
              <Form.Label className="small text-muted mb-1">To Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Col>

            <Col lg={1} md={2} xs={12} className="d-flex gap-1">
              <Button variant="primary" size="sm" className="w-100" onClick={loadClaims}>
                Filter
              </Button>
            </Col>
          </Row>

          {/* Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="text-muted small mt-2">Loading claims...</div>
            </div>
          ) : claims.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div className="fw-semibold mt-2">No reimbursement claims found</div>
              <div className="small">Try adjusting filters or click "Raise Expense Claim" to submit a new one.</div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover className="align-middle mb-0" style={{ fontSize: 13 }}>
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: 44 }} className="text-center">#</th>
                    <th>Claim ID</th>
                    <th>Date</th>
                    {activeTab !== "my" && <th>Staff Member</th>}
                    <th>Category</th>
                    <th>Merchant / Purpose</th>
                    <th className="text-end">Amount</th>
                    <th className="text-center">Bills</th>
                    <th className="text-center">Status</th>
                    <th className="text-center" style={{ minWidth: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim, index) => {
                    const isOwnClaim = currentUser && String(claim.user?._id) === String(currentUser._id);

                    return (
                      <tr key={claim._id}>
                        <td className="text-center text-muted small">{index + 1}</td>
                        <td>
                          <strong>{claim.claimId || `CLM-${claim._id.slice(-6)}`}</strong>
                          {claim.project && (
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {claim.project.projectName || claim.project.name}
                            </div>
                          )}
                        </td>
                        <td>
                          {new Date(claim.expenseDate).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        {activeTab !== "my" && (
                          <td>
                            <strong>{claim.employee?.name || claim.user?.name || "Staff"}</strong>
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {claim.employee?.level || "Employee"}
                            </div>
                          </td>
                        )}
                        <td>
                          <Badge bg="light" text="dark" className="border">
                            {claim.category}
                          </Badge>
                        </td>
                        <td>
                          <div className="fw-semibold">{claim.merchant || "-"}</div>
                          <div className="text-muted text-truncate" style={{ maxWidth: 200, fontSize: 11 }} title={claim.description}>
                            {claim.description || "No description"}
                          </div>
                        </td>
                        <td className="text-end">
                          <strong className="text-dark">{formatMoney(claim.amount)}</strong>
                          {claim.approvedAmount !== null && claim.approvedAmount !== claim.amount && (
                            <div className="text-success small" title="Approved Amount">
                              Appr: {formatMoney(claim.approvedAmount)}
                            </div>
                          )}
                        </td>
                        <td className="text-center">
                          {claim.bills?.length > 0 ? (
                            <Badge bg="secondary" pill>
                              {claim.bills.length} {claim.bills.length === 1 ? "file" : "files"}
                            </Badge>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge bg={statusVariant[claim.status] || "secondary"}>
                            {claim.status}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center">
                            <Dropdown>
                              <Dropdown.Toggle variant="" as="div" className="btn-link i-false" style={{ cursor: "pointer" }}>
                                {svgMenu}
                              </Dropdown.Toggle>

                              <Dropdown.Menu align="end" as={PortalMenu}>
                                <Dropdown.Item onClick={() => setSelectedDetailClaim(claim)}>
                                  <i className="fas fa-eye me-2 text-primary"></i> View Details
                                </Dropdown.Item>

                                {canReview && claim.status === "PENDING" && (
                                  <Dropdown.Item onClick={() => setSelectedDetailClaim(claim)}>
                                    <i className="fas fa-check-circle me-2 text-warning"></i> Review & Approve
                                  </Dropdown.Item>
                                )}

                                {canSettle && claim.status === "APPROVED" && (
                                  <Dropdown.Item onClick={() => setSelectedDetailClaim(claim)}>
                                    <i className="fas fa-money-bill-wave me-2 text-success"></i> Settle & Pay
                                  </Dropdown.Item>
                                )}

                                {((isOwnClaim && ["DRAFT", "PENDING"].includes(claim.status)) || canEdit) && claim.status !== "SETTLED" && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      setSelectedEditClaim(claim);
                                      setShowCreateModal(true);
                                    }}
                                  >
                                    <i className="fas fa-edit me-2 text-info"></i> Edit Claim
                                  </Dropdown.Item>
                                )}

                                {((isOwnClaim && ["DRAFT", "PENDING"].includes(claim.status)) || canDelete) && claim.status !== "SETTLED" && (
                                  <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() => handleDeleteClaim(claim)}
                                    >
                                      <i className="fas fa-trash me-2"></i> Delete Claim
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modals */}
      <ReimbursementFormModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setSelectedEditClaim(null);
        }}
        claim={selectedEditClaim}
        onSuccess={() => {
          loadClaims();
          loadStats();
        }}
      />

      <ReimbursementDetailModal
        show={Boolean(selectedDetailClaim)}
        onHide={() => setSelectedDetailClaim(null)}
        claim={selectedDetailClaim}
        canReview={canReview}
        canSettle={canSettle}
        onSuccess={() => {
          loadClaims();
          loadStats();
        }}
      />
    </div>
  );
};

export default ReimbursementPage;
